import { useState } from 'react';
import type { FindReplaceOptions } from './ops';
import './EditorToolbar.css';

interface EditorToolbarProps {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** Runs the replacement; returns the match count, or throws on an invalid regex. */
  onReplaceAll: (find: string, replace: string, opts: FindReplaceOptions) => number;
  /**
   * Live find-as-filter (cue-forge-q51): fires whenever the Find text or the
   * regex option changes, so Studio can filter the cue list to matching cues.
   */
  onFilterChange?: (find: string, opts: FindReplaceOptions) => void;
}

/**
 * Editor toolbar (spec §4.1 #3): find/replace (plain + regex) and undo/redo. The
 * Find field doubles as a live cue filter (cue-forge-q51) — typing narrows the
 * table to matching cues while still targeting replace-all. Merge/split live as
 * per-row actions in the cue table.
 */
export function EditorToolbar({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onReplaceAll,
  onFilterChange,
}: EditorToolbarProps) {
  const [find, setFind] = useState('');
  const [replace, setReplace] = useState('');
  const [regex, setRegex] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function changeFind(next: string) {
    setFind(next);
    onFilterChange?.(next, { regex });
  }

  function changeRegex(next: boolean) {
    setRegex(next);
    onFilterChange?.(find, { regex: next });
  }

  function replaceAll() {
    try {
      const count = onReplaceAll(find, replace, { regex });
      setError(null);
      setStatus(`${count} replaced`);
      // Clear the find filter (cue-forge-q51): the replaced cues no longer match
      // the old query, so they would vanish from a filtered list — drop the filter
      // so the results stay visible.
      setFind('');
      onFilterChange?.('', { regex });
    } catch {
      setStatus(null);
      setError('Invalid pattern');
    }
  }

  return (
    <div className="cf-toolbar">
      <div className="cf-toolbar__group">
        <button
          type="button"
          className="cf-btn"
          onClick={onUndo}
          disabled={!canUndo}
          aria-label="Undo"
          title="Undo (Ctrl+Z)"
          aria-keyshortcuts="Control+Z"
        >
          Undo
        </button>
        <button
          type="button"
          className="cf-btn"
          onClick={onRedo}
          disabled={!canRedo}
          aria-label="Redo"
          title="Redo (Ctrl+Y)"
          aria-keyshortcuts="Control+Y Control+Shift+Z"
        >
          Redo
        </button>
      </div>

      <div className="cf-toolbar__group cf-toolbar__find">
        <input
          className="cf-input cf-mono"
          aria-label="Find"
          placeholder="Find"
          value={find}
          onChange={(e) => changeFind(e.target.value)}
        />
        <input
          className="cf-input cf-mono"
          aria-label="Replace"
          placeholder="Replace"
          value={replace}
          onChange={(e) => setReplace(e.target.value)}
        />
        <label className="cf-toolbar__check">
          <input type="checkbox" checked={regex} onChange={(e) => changeRegex(e.target.checked)} />
          Regex
        </label>
        <button type="button" className="cf-btn" onClick={replaceAll} disabled={find === ''}>
          Replace all
        </button>
        {/* Always-mounted polite live region so replace-all results are
            announced; a region mounted together with its message is not. */}
        <span className="cf-toolbar__status" role="status" aria-live="polite">
          {status ?? ''}
        </span>
        {error && (
          <span className="cf-toolbar__error" role="alert">
            {error}
          </span>
        )}
      </div>
    </div>
  );
}
